import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { interval, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { LaunchContext } from '../../../../services/unified-vm-launcher.service';
import { OrgTemplateAadService } from '../../../../services/org-template-aad.service';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../injection-tokens/entity-service-tokens';
import { IClassesEntityService } from '../../../../interfaces/entities/classes-entity-service.interface';
import { Log } from '../../../../services/logger.service';

/**
 * Display states for the AAD provisioning card inside the VM connection drawer.
 *
 * - `no-ua-template` — No UA template attached; section not rendered.
 * - `not-provisioned` — UA template attached but credentials not yet issued.
 * - `active` — Credentials resolved; parent renders the active credentials block.
 */
export type AadCardState = 'no-ua-template' | 'not-provisioned' | 'active';

const POLL_INTERVAL_MS = 5000;

@Component({
  selector: 'app-vm-aad-status-card',
  templateUrl: './vm-aad-status-card.component.html',
  styleUrls: ['./vm-aad-status-card.component.css'],
})
export class VmAadStatusCardComponent implements OnChanges, OnDestroy {
  @Input() classId: string | null = null;
  @Input() participantId: string | null = null;
  @Input() uaTemplateId: string | null = null;
  @Input() launchContext: LaunchContext = 'participant-self-vm';

  @Output() credentialsReady = new EventEmitter<{ userPrincipalName: string; temporaryAccessPass: string }>();

  state: AadCardState = 'no-ua-template';
  isProvisioning = false;
  provisionError: string | null = null;

  private readonly destroy$ = new Subject<void>();
  private readonly stopPolling$ = new Subject<void>();
  private readonly logger = Log.create(VmAadStatusCardComponent);

  constructor(
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classesService: IClassesEntityService,
    private readonly aadService: OrgTemplateAadService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['classId'] || changes['participantId'] || changes['uaTemplateId'] || changes['launchContext']) {
      this.stopPolling$.next();
      this.evaluateState();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling$.next();
    this.stopPolling$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private evaluateState(): void {
    const hasTemplate = this.isParticipantOrOperatorContext
      ? !!(this.classId && this.participantId)
      : !!this.uaTemplateId;

    if (!hasTemplate) {
      this.state = 'no-ua-template';
      return;
    }

    this.state = 'not-provisioned';
    this.checkCurrentStatus();
  }

  private get isParticipantOrOperatorContext(): boolean {
    return this.launchContext === 'participant-self-vm' || this.launchContext === 'operator-remote-vm';
  }

  /** Whether the provision button should be enabled for the current context. */
  get canProvision(): boolean {
    // Only participant-self can provision; operator-remote is read-only
    return this.launchContext === 'participant-self-vm' && !!this.classId && !!this.participantId;
  }

  private checkCurrentStatus(): void {
    if (this.isParticipantOrOperatorContext) {
      this.checkParticipantStatus();
    } else {
      this.checkTemplateStatus();
    }
  }

  private checkParticipantStatus(): void {
    if (!this.classId || !this.participantId) return;
    this.classesService.getCloudUserAccount(this.classId, this.participantId, false).subscribe({
      next: (res: any) => {
        if (res?.username && res?.temporaryAccessPass) {
          this.emitCredentials(res.username, res.temporaryAccessPass);
        } else {
          this.startPolling();
        }
      },
      error: () => this.startPolling(),
    });
  }

  private checkTemplateStatus(): void {
    if (!this.uaTemplateId) return;
    this.aadService.getStatus(this.uaTemplateId).subscribe({
      next: (status) => {
        if (status?.status === 'Active') {
          this.fetchTemplateCredentials();
        } else {
          this.startPolling();
        }
      },
      error: () => this.startPolling(),
    });
  }

  /** Triggers provisioning via the appropriate endpoint for the current context. */
  provision(): void {
    // Stop any existing polling before starting new provisioning
    this.stopPolling$.next();

    if (this.launchContext === 'operator-remote-vm') {
      // Operator-remote is read-only, don't provision
      this.logger.w('Operator remote context does not support provisioning');
      return;
    }

    if (this.isParticipantOrOperatorContext) {
      this.provisionParticipant();
    } else {
      this.provisionTemplate();
    }
  }

  private provisionParticipant(): void {
    if (!this.classId || !this.participantId) return;
    this.isProvisioning = true;
    this.provisionError = null;

    this.classesService.getCloudUserAccount(this.classId, this.participantId, true).subscribe({
      next: (res: any) => {
        this.isProvisioning = false;
        if (res?.username && res?.temporaryAccessPass) {
          this.emitCredentials(res.username, res.temporaryAccessPass);
        } else {
          this.startPolling();
        }
      },
      error: (err: any) => {
        this.isProvisioning = false;
        if (err?.status === 404) {
          this.startPolling();
        } else {
          this.provisionError = err?.error?.message ?? 'Failed to initiate provisioning.';
          this.logger.e('Provision error:', err);
        }
      },
    });
  }

  private provisionTemplate(): void {
    if (!this.uaTemplateId) return;
    this.isProvisioning = true;
    this.provisionError = null;

    this.aadService.triggerProvision(this.uaTemplateId).subscribe({
      next: () => {
        this.isProvisioning = false;
        this.startPolling();
      },
      error: (err: any) => {
        this.isProvisioning = false;
        this.provisionError = err?.error?.message ?? 'Failed to initiate provisioning.';
        this.logger.e('Template provision error:', err);
      },
    });
  }

  private startPolling(): void {
    this.stopPolling$.next();

    if (this.isParticipantOrOperatorContext) {
      this.pollParticipantStatus();
    } else {
      this.pollTemplateStatus();
    }
  }

  private pollParticipantStatus(): void {
    if (!this.classId || !this.participantId) return;
    const classId = this.classId;
    const participantId = this.participantId;

    interval(POLL_INTERVAL_MS).pipe(
      takeUntil(this.stopPolling$),
      takeUntil(this.destroy$),
      switchMap(() => this.classesService.getCloudUserAccount(classId, participantId, false)),
    ).subscribe({
      next: (res: any) => {
        if (res?.username && res?.temporaryAccessPass) {
          this.stopPolling$.next();
          this.emitCredentials(res.username, res.temporaryAccessPass);
        }
      },
      error: (err: any) => this.logger.e('Participant status poll error:', err),
    });
  }

  private pollTemplateStatus(): void {
    if (!this.uaTemplateId) return;
    const templateId = this.uaTemplateId;

    interval(POLL_INTERVAL_MS).pipe(
      takeUntil(this.stopPolling$),
      takeUntil(this.destroy$),
      switchMap(() => this.aadService.getStatus(templateId)),
    ).subscribe({
      next: (status) => {
        if (status?.status === 'Active') {
          this.stopPolling$.next();
          this.fetchTemplateCredentials();
        }
      },
      error: (err: any) => this.logger.e('Template status poll error:', err),
    });
  }

  private fetchTemplateCredentials(): void {
    if (!this.uaTemplateId || !this.classId) return;
    this.aadService.getClassTemplateCredentials(this.uaTemplateId, this.classId).subscribe({
      next: (creds) => {
        this.emitCredentials(creds.userPrincipalName, creds.temporaryAccessPass);
      },
      error: (err: any) => {
        this.logger.e('Failed to fetch template credentials:', err);
      },
    });
  }

  private emitCredentials(userPrincipalName: string, temporaryAccessPass: string): void {
    this.state = 'active';
    this.credentialsReady.emit({ userPrincipalName, temporaryAccessPass });
  }
}
